import createDOMPurify, { type Config, type DOMPurify } from "dompurify";

/**
 * Post HTML comes from our own Ghost CMS but is still untrusted markup, so it
 * is sanitized before it reaches dangerouslySetInnerHTML.
 *
 * DOMPurify is the one library used everywhere; only the DOM it runs on
 * differs: the browser's own `window`, or (at build time, in
 * scripts/prerender.mjs) a jsdom window registered via setServerDomPurify().
 * It is not bundled into the SSR output: the prerender script owns the DOM.
 * With neither available the result is "" (fail closed), never raw HTML.
 */
export const SANITIZE_OPTIONS: Config = {
  USE_PROFILES: { html: true },
  // The html profile still allows form controls and inline styles; a blog
  // post needs neither. iframes (embeds) are off by default and stay off.
  FORBID_TAGS: ["style", "form", "input", "button", "textarea", "select"],
  FORBID_ATTR: ["style"],
  // Kept so authors can open links in a new tab; the hook below adds rel.
  ADD_ATTR: ["target"],
};

/** Host of the Ghost CMS for the call in progress (sanitizing is synchronous). */
let cmsHost: string | null = null;

const POST_SLUG = /^\/([a-z0-9][a-z0-9-]*)\/?$/i;
const TAG_SLUG = /^\/tag\/([a-z0-9][a-z0-9-]*)\/?$/i;

/**
 * Links Ghost editors paste point at the CMS (`https://cms.example.com/slug/`).
 * Those pages are not ours: `/slug/` becomes `/blog/slug` (no trailing slash,
 * same host as the site) and `/tag/x/` becomes `/blog/tag/x`.
 */
export function rewriteCmsHref(href: string, host: string | null): string {
  if (!host) return href;
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return href;
  }
  if (url.host !== host) return href;
  const post = POST_SLUG.exec(url.pathname)?.[1];
  const tag = TAG_SLUG.exec(url.pathname)?.[1];
  const path = tag ? `/blog/tag/${tag}` : post ? `/blog/${post}` : null;
  return path ? `${path}${url.search}${url.hash}` : href;
}

let browserPurify: DOMPurify | null = null;
let serverPurify: DOMPurify | null = null;

/** Opens links safely; installed once on each DOMPurify instance. */
function configure(purify: DOMPurify): DOMPurify {
  purify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A" && node.hasAttribute("href")) {
      const href = node.getAttribute("href") ?? "";
      const next = rewriteCmsHref(href, cmsHost);
      if (next !== href) {
        node.setAttribute("href", next);
        node.removeAttribute("target");
      }
    }
    if (node.tagName === "A" && node.getAttribute("target") === "_blank") {
      node.setAttribute("rel", "noopener noreferrer");
    }
  });
  return purify;
}

/** Build time only: hand in a DOMPurify instance bound to a jsdom window. */
export function setServerDomPurify(purify: DOMPurify | null): void {
  serverPurify = purify ? configure(purify) : null;
}

function activePurify(): DOMPurify | null {
  if (typeof window !== "undefined") {
    if (!browserPurify) {
      const candidate = createDOMPurify(window);
      browserPurify = candidate.isSupported ? configure(candidate) : null;
    }
    return browserPurify;
  }
  return serverPurify?.isSupported ? serverPurify : null;
}

/** The page has its own <h1> (the post title); body headings start at h2. */
export function demoteH1(html: string): string {
  return html.replace(/<(\/?)h1(?=[\s>])/gi, "<$1h2");
}

export function sanitizePostHtml(
  html: string,
  { cmsHost: host = null }: { cmsHost?: string | null } = {},
): string {
  if (!html) return "";
  const purify = activePurify();
  if (!purify) return "";
  cmsHost = host;
  try {
    return demoteH1(purify.sanitize(html, SANITIZE_OPTIONS));
  } finally {
    cmsHost = null;
  }
}
